import { Modal } from "@mui/material";
import { lowerCase } from "lodash";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { ConfirmResult, dataChangedConfirmRideAtom, Log } from "@ryde/shared";

import {
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
  MuiButton,
} from "../controls";
import { ConfirmPassengerDeleteContent } from "./confirms.styled";

const MODAL_WIDTH = "530px";

export const ConfirmDataChangedRideContainer: FC = () => {
  const { t } = useTranslation();
  const [confirmData, setConfirmData] = useRecoilState(
    dataChangedConfirmRideAtom,
  );

  const isModalOpen = useMemo(() => {
    return !!confirmData;
  }, [confirmData]);

  const okClickHandler = useCallback(
    (result: ConfirmResult) => () => {
      if (!confirmData) {
        Log.warnAndToast(t("errors.INVALID_DELETE_HANDLER_DATA"));
        return;
      }

      const { resolveFn } = confirmData;
      resolveFn(result);
      Log.debug("Data confirm with result:", result, confirmData.changesList);
      setConfirmData(null);
    },
    [confirmData, setConfirmData, t],
  );

  return (
    <>
      {isModalOpen && (
        <Modal open={isModalOpen || false} closeAfterTransition={true}>
          <ModalContainerStyled width={MODAL_WIDTH}>
            <ModalHeaderStyled>
              <h1>{t(`general.UPDATE_SUMMARY_TITLE`)}</h1>
              <SVG_MUI.XMarkSVG
                onClick={okClickHandler(ConfirmResult.CANCEL)}
              />
            </ModalHeaderStyled>
            <ModalContentStyled>
              <ConfirmPassengerDeleteContent>
                <ul>
                  {confirmData?.changesList?.map(changeItem => (
                    <li key={changeItem.label}>
                      <label>{changeItem.label}</label>
                      {changeItem.oldLabel !== "" && (
                        <label>{lowerCase(t(`general.UPDATE_FROM`))}</label>
                      )}
                      <span>{changeItem.oldLabel} </span>
                      {changeItem.newLabel !== "" && (
                        <label>{lowerCase(t(`general.UPDATE_TO`))}</label>
                      )}
                      <span>{changeItem.newLabel}</span>
                    </li>
                  ))}
                </ul>
              </ConfirmPassengerDeleteContent>
            </ModalContentStyled>
            <ModalFooterStyled>
              <MuiButton
                muiStyle={"primary"}
                muiVariant={"outlined"}
                onClick={okClickHandler(ConfirmResult.CANCEL)}
                e2eData="cancel-modal-button"
              >
                {t(`general.CANCEL`)}
              </MuiButton>
              <MuiButton
                muiVariant={"outlined"}
                muiStyle={"warning"}
                onClick={okClickHandler(ConfirmResult.OK)}
                e2eData="accept-data-change"
              >
                {t(`general.ACCEPT`)}
              </MuiButton>
            </ModalFooterStyled>
          </ModalContainerStyled>
        </Modal>
      )}
    </>
  );
};
